import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type MouseEvent,
  type ReactNode,
} from "react";
import { DICT, type DictKey, type Lang } from "./_home/dict";
import { text, items, bullets, lines, image } from "./_home/content";
import { submitLead } from "./lib/api";
import { fetchOffers, formatPrice, type Offer, type SiteSnapshot } from "./lib/products";
import { homeHref, tripHref, personaHref, isPlainClick } from "./lib/nav";
import TripPage from "./TripPage";
import PersonaPage, { isPersona, PERSONA_SLUGS, type Persona } from "./PersonaPage";

const LOGO_BLUE = "/brand/logo-blue.svg";
const LOGO_CREAM = "/brand/logo-cream.svg";

const PERSONA_IMAGES = [
  "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=900&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1493558103817-58b2924bce98?w=900&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=900&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1551918120-9739cb430c6d?w=900&q=80&auto=format&fit=crop",
];

const HERO_POSTER =
  "https://images.unsplash.com/photo-1509840841025-9088ba78a826?auto=format&fit=crop&w=2400&q=80";

const HERO_VIDEO_SOURCES = [
  "https://videos.pexels.com/video-files/2169880/2169880-uhd_2560_1440_30fps.mp4",
  "https://interactive-examples.mdn.mozilla.net/media/cc0-videos/flower.mp4",
];

const CLOSING_BG =
  "https://images.unsplash.com/photo-1505142468610-359e7d316be0?w=2200&q=85&auto=format&fit=crop";

const WTS_IMAGES = [
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1200&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1534080564583-6be75777b70a?w=1200&q=80&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1551918120-9739cb430c6d?w=1200&q=80&auto=format&fit=crop",
];

const MEGA_FEATURE_IMG =
  "https://images.unsplash.com/photo-1543783207-ec64e4d95325?w=1000&q=80&auto=format&fit=crop";

const MEGA_PERSONA_THUMBS = [
  "https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e?w=300&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1493558103817-58b2924bce98?w=300&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=300&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1551918120-9739cb430c6d?w=300&q=70&auto=format&fit=crop",
];

const TESTIMONY_AVATARS = [
  "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=200&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&q=70&auto=format&fit=crop",
  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&q=70&auto=format&fit=crop",
];

type SubMsg = { kind: "ok" | "err"; text: string } | null;

// Placeholder click handler for links that don't have real targets yet.
// Prevents the default jump-to-top behavior of `href="#"`.
const stub = (e: MouseEvent<HTMLAnchorElement>) => e.preventDefault();

const TRIP_PATH = /^(?:\/es)?\/trip\/([^/?#]+)\/?$/;
const PERSONA_PATH = /^(?:\/es)?\/personas\/([^/?#]+)\/?$/;

/** Server-passable initial state — populated at build time by prerender.mjs
 *  and at runtime by entry-client from the <script id="__bougie-state__"> tag. */
export interface AppInitialState {
  /** Pathname being rendered, e.g. "/" or "/trip/altea-guadalest/". */
  url: string;
  /** Offers snapshot from HighLevel, embedded at build time. */
  offers: Offer[] | null;
  /** True if the build-time fetch errored. Browser still tries to refresh. */
  offersError?: boolean;
  /** Editable site content snapshot keyed by section slug. */
  site?: SiteSnapshot;
}

function pathToSlug(pathname: string): string | null {
  const m = pathname.match(TRIP_PATH);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
}

function pathToLang(pathname: string): Lang {
  return pathname === "/es" || pathname.startsWith("/es/") ? "es" : "en";
}

function pathToPersona(pathname: string): Persona | null {
  const m = pathname.match(PERSONA_PATH);
  if (!m) return null;
  let slug: string;
  try {
    slug = decodeURIComponent(m[1]!);
  } catch {
    return null;
  }
  return isPersona(slug) ? slug : null;
}

/** Splits a string into everything-before-the-last-line + last-line.
 *  Used to peel the attribution off a testimonial whose preceding lines
 *  hold the quote (which may itself span multiple lines). */
function splitLastLine(s: string): [string, string] {
  const i = s.lastIndexOf("\n");
  if (i < 0) return [s.trim(), ""];
  return [s.slice(0, i).trim(), s.slice(i + 1).trim()];
}

/** Truncate at a word boundary, preserving grapheme integrity. */
function truncateAtWord(s: string, max: number): string {
  if (s.length <= max) return s;
  const head = s.slice(0, max);
  const lastSpace = head.lastIndexOf(" ");
  const cut = lastSpace > max * 0.6 ? head.slice(0, lastSpace) : head;
  return `${cut.trim()}…`;
}

/** Split a headline string on `*word*` italic-accent marker. Returns
 *  pre + italic word + post; if no marker, the whole string is `pre`. */
function splitHeadline(s: string): { pre: string; it: string; post: string } {
  const m = /\*([^*]+)\*/.exec(s);
  if (!m) return { pre: s, it: "", post: "" };
  return {
    pre: s.slice(0, m.index),
    it: m[1]!,
    post: s.slice(m.index + m[0].length),
  };
}

/** Render a headline supporting `||` line breaks and `*word*` coral accents.
 *  e.g. "A long lunch,||somewhere *else*." → two lines, "else" accented. */
function renderHeadline(headline: string) {
  const accents = (line: string) => {
    const out: ReactNode[] = [];
    const re = /\*([^*]+)\*/g;
    let last = 0;
    let m: RegExpExecArray | null;
    let i = 0;
    while ((m = re.exec(line)) !== null) {
      if (m.index > last) out.push(line.slice(last, m.index));
      out.push(
        <span key={`a${i++}`} className="amp">
          {m[1]}
        </span>,
      );
      last = m.index + m[0].length;
    }
    if (last < line.length) out.push(line.slice(last));
    return out;
  };
  return headline.split("||").map((line, li) => (
    <span key={li} className="hl-line">
      {accents(line.trim())}
    </span>
  ));
}

/** Renders a quote string with `**bold**` markers as the `.roman` accent.
 *  Strips a leading `>` blockquote marker if present. */
function renderQuoteWithBold(quote: string) {
  const q = quote.replace(/^>\s?/, "").trim();
  const parts: Array<string | { bold: string }> = [];
  let i = 0;
  const re = /\*\*([^*]+)\*\*/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(q)) !== null) {
    if (m.index > i) parts.push(q.slice(i, m.index));
    parts.push({ bold: m[1]! });
    i = m.index + m[0].length;
  }
  if (i < q.length) parts.push(q.slice(i));
  return (
    <>
      {parts.map((p, idx) =>
        typeof p === "string" ? (
          <span key={idx}>{p}</span>
        ) : (
          <span key={idx} className="roman">
            {p.bold}
          </span>
        ),
      )}
    </>
  );
}

export default function App({ initial }: { initial: AppInitialState }) {
  const initialSlug = pathToSlug(initial.url);
  const [lang, setLang] = useState<Lang>(() => pathToLang(initial.url));
  const [spotIdx, setSpotIdx] = useState(0);
  const [megaOpen, setMegaOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [subEmail, setSubEmail] = useState("");
  const [subBusy, setSubBusy] = useState(false);
  const [subMsg, setSubMsg] = useState<SubMsg>(null);
  // Offers start populated from the build-time snapshot so the first paint
  // has real product data. Client may still refresh after hydration.
  const [offers, setOffers] = useState<Offer[] | null>(initial.offers);
  const [offersError, setOffersError] = useState(!!initial.offersError);
  const [tripSlug, setTripSlug] = useState<string | null>(initialSlug);
  const [personaSlug, setPersonaSlug] = useState<Persona | null>(() => pathToPersona(initial.url));

  const heroVideoRef = useRef<HTMLVideoElement | null>(null);
  const heroPosterRef = useRef<HTMLImageElement | null>(null);
  const spotlightRef = useRef<HTMLElement | null>(null);

  const t = useCallback(
    (key: DictKey): string => DICT[lang][key],
    [lang],
  );

  const site = initial.site;
  // Section content helpers bound to the current locale + site snapshot.
  // Each falls back to a dict / hardcoded default if the HL section is missing.
  const cText = useCallback(
    (slug: string, key: string, fallback: string) =>
      text(site, lang, slug, key, fallback),
    [site, lang],
  );
  const cItems = useCallback(
    (slug: string, key: string) => items(site, lang, slug, key),
    [site, lang],
  );
  // Image URLs are not localized — always read from the EN block, with the
  // bundled Unsplash constant as the fallback default.
  const cImage = useCallback(
    (slug: string, key: string, fallback: string) => image(site, slug, key, fallback),
    [site],
  );
  const cLines = useCallback(
    (slug: string, key: string) => lines(site, lang, slug, key),
    [site, lang],
  );
  // Reserved for sections that author bullets via `-` lists.
  void bullets;

  // Featured spotlights — Trip records with featured_order set, sorted
  // ascending, capped at 4. Falls back to the first 4 offers if nothing's
  // explicitly featured yet (so the section never goes empty).
  const featured = useMemo(() => {
    if (!offers || offers.length === 0) return [] as Offer[];
    const ranked = offers
      .filter((o) => typeof o.featuredOrder === "number")
      .sort((a, b) => (a.featuredOrder! - b.featuredOrder!));
    const pool = ranked.length > 0 ? ranked : offers;
    return pool.slice(0, 4);
  }, [offers]);
  const safeSpotIdx = featured.length > 0 ? spotIdx % featured.length : 0;
  const spot = featured[safeSpotIdx];

  // Reveal-on-scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    document.querySelectorAll<HTMLElement>("[data-anim]").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Topbar solidify + hero parallax. RAF-throttled so we only do work once
  // per frame, and setScrolled only fires when the threshold actually flips.
  useEffect(() => {
    let raf = 0;
    let lastSolid = false;
    const tick = () => {
      raf = 0;
      const y = window.scrollY;
      const nextSolid = y > 40;
      if (nextSolid !== lastSolid) {
        lastSolid = nextSolid;
        setScrolled(nextSolid);
      }
      if (y < 1100) {
        const transform = `translateY(${y * 0.18}px) scale(1.05)`;
        if (heroVideoRef.current) heroVideoRef.current.style.transform = transform;
        if (heroPosterRef.current) heroPosterRef.current.style.transform = transform;
      }
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(tick);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Lock scroll + listen for Escape while megamenu open
  useEffect(() => {
    if (!megaOpen) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMegaOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKey);
    };
  }, [megaOpen]);

  // Sync <html lang>
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  // Path-based router: /trip/<slug>/ (or /es/trip/<slug>/) opens the trip
  // page. Each route has its own prerendered HTML in dist/, so a direct
  // visit lands on the right page; React just picks up from there. Pop
  // events also re-derive the locale so back/forward across /es preserves
  // language.
  useEffect(() => {
    const onPop = () => {
      const path = window.location.pathname;
      setTripSlug(pathToSlug(path));
      setPersonaSlug(pathToPersona(path));
      setLang(pathToLang(path));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const langPrefix = lang === "es" ? "/es" : "";

  const goToTrip = useCallback(
    (slug: string) => {
      const path = `${langPrefix}/trip/${encodeURIComponent(slug)}/`;
      history.pushState(null, "", path);
      setPersonaSlug(null);
      setTripSlug(slug);
      window.scrollTo({ top: 0, behavior: "auto" });
    },
    [langPrefix],
  );

  const goToPersona = useCallback(
    (persona: Persona) => {
      const path = `${langPrefix}/personas/${persona}/`;
      history.pushState(null, "", path);
      setTripSlug(null);
      setPersonaSlug(persona);
      window.scrollTo({ top: 0, behavior: "auto" });
    },
    [langPrefix],
  );

  const goHome = useCallback(() => {
    history.pushState(null, "", langPrefix === "" ? "/" : `${langPrefix}/`);
    setTripSlug(null);
    setPersonaSlug(null);
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [langPrefix]);

  // Turn an in-app navigation fn into an anchor onClick: intercept only a
  // plain left-click (so the instant pushState transition fires) and let
  // modified / middle clicks fall through to the real href, so open-in-new-tab
  // and friends behave like any normal link.
  const navClick = useCallback(
    (go: () => void) => (e: MouseEvent<HTMLAnchorElement>) => {
      if (!isPlainClick(e)) return;
      e.preventDefault();
      go();
    },
    [],
  );

  // Locale toggle: rewrites the URL so /trip/<slug>/ ↔ /es/trip/<slug>/
  // (and / ↔ /es/) stay consistent with the active language. Updates
  // history without a full navigation; the React tree re-renders in-place.
  const switchLang = useCallback((next: Lang) => {
    if (typeof window === "undefined") return;
    const pathname = window.location.pathname;
    const stripped = pathname.replace(/^\/es(?=\/|$)/, "") || "/";
    const target = next === "es"
      ? (stripped === "/" ? "/es/" : `/es${stripped}`)
      : stripped;
    if (target !== pathname) {
      history.replaceState(null, "", target);
    }
    setLang(next);
  }, []);

  // Background refresh: editorial trip content/photos are baked at build time
  // from the repo records (the source of truth). The runtime API now serves
  // only live commercial data (price/availability), so we MERGE price by slug
  // into the baked offers rather than replacing them — keeping content intact
  // while reflecting price changes made in HighLevel between deploys. Falls
  // back silently to the build snapshot on failure.
  useEffect(() => {
    let cancelled = false;
    fetchOffers()
      .then((fresh) => {
        if (cancelled || fresh.length === 0) return;
        setOffers((prev) => {
          // No baked snapshot (e.g. dev server) — use the live list as-is.
          if (!prev || prev.length === 0) return fresh;
          const bySlug = new Map(
            fresh.filter((f) => f.slug).map((f) => [f.slug as string, f]),
          );
          return prev.map((o) => {
            const live = o.slug ? bySlug.get(o.slug) : undefined;
            return live ? { ...o, price: live.price } : o;
          });
        });
      })
      .catch(() => {
        /* keep the build-time snapshot */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubscribe = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!subEmail || subBusy) return;
      setSubBusy(true);
      setSubMsg(null);
      try {
        const res = await submitLead({
          email: subEmail,
          source: "footer-newsletter",
          locale: lang,
          tags: ["newsletter"],
        });
        if (res.ok) {
          setSubMsg({ kind: "ok", text: t("foot_sub_ok") });
          setSubEmail("");
        } else {
          setSubMsg({ kind: "err", text: t("foot_sub_err") });
        }
      } catch {
        setSubMsg({ kind: "err", text: t("foot_sub_err") });
      } finally {
        setSubBusy(false);
      }
    },
    [subEmail, subBusy, lang, t],
  );

  if (tripSlug) {
    return (
      <TripPage
        slug={tripSlug}
        lang={lang}
        onBack={goHome}
        offers={offers}
        offersError={offersError}
      />
    );
  }

  if (personaSlug) {
    return (
      <PersonaPage
        persona={personaSlug}
        lang={lang}
        site={site}
        offers={offers}
        offersError={offersError}
        onHome={goHome}
        onTrip={goToTrip}
      />
    );
  }

  return (
    <>
      {/* ===== Top bar ===== */}
      <header className={`topbar${scrolled ? " solid" : ""}`}>
        <nav className="nav-left">
          <button
            type="button"
            className="menu-trig"
            aria-label="Open menu"
            aria-expanded={megaOpen}
            aria-controls="bougie-megamenu"
            onClick={() => setMegaOpen(true)}
          >
            <svg
              className="menu-icon"
              width="18"
              height="14"
              viewBox="0 0 18 14"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            >
              <path d="M1 1.5h16M1 7h16M1 12.5h16" />
            </svg>
            <span>{t("nav_explore")}</span>
          </button>
          <a href="#" onClick={stub}>{t("nav_club")}</a>
          <a href="#" onClick={stub}>{t("nav_field")}</a>
        </nav>
        <a
          href={homeHref(lang)}
          onClick={navClick(goHome)}
          className="wordmark"
          aria-label="Bougie Travel Club, home"
        >
          <img
            className="wordmark-logo"
            src={scrolled ? LOGO_BLUE : LOGO_CREAM}
            alt="The Bougie Travel Club"
          />
        </a>
        <nav className="nav-right">
          <a href="#" onClick={stub}>{t("signin")}</a>
          <div className="lang-toggle">
            <button
              type="button"
              className={lang === "en" ? "is-on" : undefined}
              onClick={() => switchLang("en")}
            >
              EN
            </button>
            <button
              type="button"
              className={lang === "es" ? "is-on" : undefined}
              onClick={() => switchLang("es")}
            >
              ES
            </button>
          </div>
        </nav>
      </header>

      {/* ===== Megamenu ===== */}
      <div
        className={`scrim${megaOpen ? " is-open" : ""}`}
        aria-hidden="true"
        onClick={() => setMegaOpen(false)}
      />
      <div
        id="bougie-megamenu"
        className={`mega-wrap${megaOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Site navigation"
        aria-hidden={!megaOpen}
      >
        <button
          type="button"
          className="mega-close"
          aria-label="Close menu"
          onClick={() => setMegaOpen(false)}
        >
          ✕ Close
        </button>
        <div className="mega-grid">
          <div>
            <h5>{cText("megamenu", "calendar_heading", t("mega_cal"))}</h5>
            <div className="mega-months">
              {(() => {
                const months = cItems("megamenu", "months");
                const fallback: Array<[string, string]> = [
                  ["March", "4 trips"],
                  ["April", "6 trips"],
                  ["May", "5 trips"],
                  ["June", "7 trips"],
                  ["September", "8 trips"],
                  ["October", "6 trips"],
                  ["November", "4 trips"],
                ];
                const rows = months.length > 0
                  ? months.map((m) => [m.title, m.body?.trim() ?? ""] as [string, string])
                  : fallback;
                return rows.map(([name, meta], i) => {
                  const metaLines = meta.split("\n").map((l) => l.trim()).filter(Boolean);
                  return (
                    <a key={`${name}-${i}`} href="#" onClick={stub}>
                      <span>{name}</span>
                      <span>
                        {metaLines.map((line, li) => (
                          <span key={li}>
                            {li > 0 && <br />}
                            {line}
                          </span>
                        ))}
                      </span>
                    </a>
                  );
                });
              })()}
            </div>
          </div>
          <div>
            <h5>{cText("megamenu", "personas_heading", t("mega_per"))}</h5>
            <div className="mega-personas">
              {(["tab_w", "tab_m", "tab_x", "tab_p"] as const).map((k, i) => (
                <a
                  key={k}
                  href={personaHref(lang, PERSONA_SLUGS[i]!)}
                  onClick={navClick(() => {
                    setMegaOpen(false);
                    goToPersona(PERSONA_SLUGS[i]!);
                  })}
                >
                  <div
                    className="thumb"
                    style={{ backgroundImage: `url('${cImage("megamenu", `persona_thumb_${i + 1}`, MEGA_PERSONA_THUMBS[i]!)}')` }}
                  />
                  <div className="label">
                    {t(k)}
                    <span className="sub">
                      {i === 3
                        ? t("meta_private_count")
                        : i === 0
                        ? "12 trips · 2026"
                        : i === 1
                        ? "8 trips · 2026"
                        : "14 trips · 2026"}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
          <div
            className="mega-feature"
            style={{ backgroundImage: `url('${cImage("megamenu", "feature_image", MEGA_FEATURE_IMG)}')` }}
          >
            <div className="scrim2" />
            <div className="content">
              <div className="kicker">
                {cText("megamenu", "feature_kicker", t("mega_feature_kicker"))}
              </div>
              <h4>{cText("megamenu", "feature_title", t("mega_feature_title"))}</h4>
              <p>{cText("megamenu", "feature_body", t("mega_feature_p"))}</p>
              <a className="btn" href="#" onClick={stub}>
                <span>{cText("megamenu", "feature_cta", t("mega_feature_cta"))}</span> →
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Hero ===== */}
      <section className="hero">
        <img
          className="hero-poster"
          ref={heroPosterRef}
          src={cImage("homepage_hero", "hero_image", HERO_POSTER)}
          alt=""
        />
        <video
          className="hero-video"
          ref={heroVideoRef}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={cImage("homepage_hero", "hero_image", HERO_POSTER)}
        >
          {HERO_VIDEO_SOURCES.map((src) => (
            <source key={src} src={src} type="video/mp4" />
          ))}
        </video>
        <div className="hero-grain" />

        <div className="hero-content">
          <div className="hero-kicker">
            <span className="line" />
            <span>{cText("homepage_hero", "eyebrow", t("hero_kicker"))}</span>
            <span className="line" />
          </div>
          {(() => {
            const headline = cText(
              "homepage_hero",
              "headline",
              `${t("hero_h1_l1")} ${t("hero_h1_l2")} ${t("hero_h1_l3")} *${t("hero_h1_l4")}*`,
            );
            // `*word*` → coral accent; `||` → line break. If no accent
            // marker is present, fall back to accenting the last word so
            // legacy records still get the coral treatment.
            let hl = headline;
            if (!/\*[^*]+\*/.test(hl)) {
              const idx = hl.lastIndexOf(" ");
              if (idx > 0) hl = `${hl.slice(0, idx + 1)}*${hl.slice(idx + 1)}*`;
            }
            return <h1 className="hero-headline">{renderHeadline(hl)}</h1>;
          })()}
          <p className="hero-deck">{cText("homepage_hero", "deck", t("hero_deck"))}</p>
          <div className="hero-cta">
            <a className="btn-primary" href="#get-started">
              <span>{cText("homepage_hero", "cta_primary", t("hero_cta1"))}</span>
            </a>
            <a className="btn-ghost" href="#where-to-start">
              <span>{cText("homepage_hero", "cta_secondary", t("hero_cta2"))}</span>
            </a>
          </div>
        </div>
      </section>

      {/* ===== Now Showing (Personas) ===== */}
      <section className="nowshowing">
        <div className="ns-head">
          <h2 className="ns-title">
            {cText("homepage_personas", "heading", `${t("ns_t1")}${t("ns_t2")}`)}
          </h2>
          <p className="ns-meta">{cText("homepage_personas", "meta", t("ns_meta"))}</p>
        </div>

        <div className="personas-grid">
          {(() => {
            const cards = cItems("homepage_personas", "cards");
            const dictFallback: DictKey[] = [
              "pc_w_title",
              "pc_m_title",
              "pc_x_title",
              "pc_p_title",
            ];
            return [0, 1, 2, 3].map((i) => {
              const num = `No. 0${i + 1}`;
              const title = cards[i]?.title?.trim() || t(dictFallback[i]!);
              return (
                <a
                  key={i}
                  className="pcard"
                  href={personaHref(lang, PERSONA_SLUGS[i]!)}
                  onClick={navClick(() => goToPersona(PERSONA_SLUGS[i]!))}
                  aria-label={`Explore ${title}`}
                >
                  <div
                    className="ph"
                    style={{ backgroundImage: `url('${cImage("homepage_personas", `persona_image_${i + 1}`, PERSONA_IMAGES[i]!)}')` }}
                  />
                  <span className="focus-tag">{t("focus_tag_idle")}</span>
                  <div className="body">
                    <div className="pnum">{num}</div>
                    <h3 className="big">{title}</h3>
                  </div>
                </a>
              );
            });
          })()}
        </div>
      </section>

      {/* ===== Why ===== */}
      <section className="why-section">
        <div className="why-head">
          <h2 className="why-title">
            {cText("homepage_why", "heading", `${t("why_t1")}${t("why_t2")}`)}
          </h2>
          <p className="why-meta">{cText("homepage_why", "meta", t("why_meta"))}</p>
        </div>
        <div className="why-grid">
          {(() => {
            const cards = cItems("homepage_why", "cards");
            return [1, 2, 3, 4].map((n) => {
              const t1 = `why${n}_t1` as DictKey;
              const t2 = `why${n}_t2` as DictKey;
              const tp = `why${n}_p` as DictKey;
              const card = cards[n - 1];
              const title = card?.title?.trim() || `${t(t1)}${t(t2)}`;
              const body = card?.body?.trim() || t(tp);
              return (
                <article key={n} className="why-card">
                  <div className="why-num">0{n}</div>
                  <h3>{title}</h3>
                  <p>{body}</p>
                </article>
              );
            });
          })()}
        </div>
      </section>

      {/* ===== Spotlight ===== */}
      <section className="spotlight" ref={spotlightRef}>
        <div className="spot-frame">
          <div className="spot-section-head">
            <h2 className="spot-section-title">
              {cText(
                "homepage_spotlight_intro",
                "heading",
                `${t("spot_section_t1")}${t("spot_section_t2")}`,
              )}
            </h2>
            {spot && (
              <span className="spot-section-meta">
                {`0${safeSpotIdx + 1} of 0${featured.length} · ${spot.name.replace(/\*/g, "")}`}
              </span>
            )}
          </div>

          {spot ? (
            <>
              {(() => {
                const headline = splitHeadline(spot.name);
                const stats: Array<[string, string]> = [];
                const dur = spot.trip?.durationDays;
                if (dur != null) {
                  stats.push([
                    String(dur),
                    dur === 1
                      ? lang === "es" ? "Día" : "Day"
                      : lang === "es" ? "Días" : "Days",
                  ]);
                }
                if (spot.trip?.groupMax != null) {
                  stats.push([
                    String(spot.trip.groupMax),
                    lang === "es" ? "Plazas máx." : "Guests max",
                  ]);
                }
                if (spot.price) {
                  stats.push([
                    formatPrice(spot.price, lang),
                    lang === "es" ? "Por persona" : "Per person",
                  ]);
                } else if (spot.recordOnly) {
                  stats.push([
                    "—",
                    lang === "es" ? "Próximamente" : "Coming soon",
                  ]);
                }
                const heroImg = spot.image ?? spot.medias[0];
                const tag = lang === "es" ? "En cartelera" : "Now Showing";
                const spotKey = spot.slug ?? spot.id;
                return (
                  <div
                    className="spot-stage big"
                    style={heroImg ? { backgroundImage: `url('${heroImg}')` } : undefined}
                  >
                    <div className="spot-overlay">
                      <div className="spot-tag">{tag}</div>
                      <h3 className="spot-headline">
                        <span>{headline.pre}</span>
                        {headline.it && <span className="it">{headline.it}</span>}
                        <span>{headline.post}</span>
                      </h3>
                      {spot.description && (
                        <p className="spot-deck">{truncateAtWord(spot.description, 240)}</p>
                      )}
                      <div className="spot-foot">
                        <div className="spot-stats">
                          {stats.map(([k, l]) => (
                            <div key={l} className="stat">
                              <div className="k">{k}</div>
                              <span className="l">{l}</span>
                            </div>
                          ))}
                        </div>
                        <a
                          className="spot-cta"
                          href={tripHref(lang, spotKey)}
                          onClick={navClick(() => goToTrip(spotKey))}
                        >
                          <span>{t("spot_cta")}</span> →
                        </a>
                      </div>
                    </div>
                  </div>
                );
              })()}

              <div className="spot-pick">
                <span className="spot-pick-lbl">
                  {cText("homepage_spotlight_intro", "pick_label", t("spot_pick_lbl"))}
                </span>
              </div>
              <div className="spot-boxes">
                {featured.map((o, i) => {
                  const meta: string[] = [];
                  if (o.trip?.durationDays != null) {
                    meta.push(
                      `${o.trip.durationDays} ${
                        o.trip.durationDays === 1
                          ? lang === "es" ? "día" : "day"
                          : lang === "es" ? "días" : "days"
                      }`,
                    );
                  }
                  if (o.price) meta.push(formatPrice(o.price, lang));
                  else if (o.recordOnly) meta.push(lang === "es" ? "próximamente" : "coming soon");
                  const thumb = o.image ?? o.medias[0];
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={`spot-box${safeSpotIdx === i ? " is-on" : ""}`}
                      onClick={() => setSpotIdx(i)}
                    >
                      <div
                        className="sb-img"
                        style={thumb ? { backgroundImage: `url('${thumb}')` } : undefined}
                      />
                      <div className="sb-body">
                        <span className="sb-num">0{i + 1}</span>
                        <span className="sb-name">{o.name.replace(/\*/g, "")}</span>
                        <span className="sb-meta">{meta.join(" · ") || "—"}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="offers-state">{t("offers_loading")}</p>
          )}
        </div>
      </section>

      {/* ===== Offers (live from HighLevel Products) ===== */}
      <section className="offers" id="offers">
        <div className="offers-head">
          <h2 className="offers-title">
            {cText("homepage_offers_intro", "heading", `${t("offers_t1")}${t("offers_t2")}`)}
          </h2>
          <p className="offers-meta">
            {cText("homepage_offers_intro", "meta", t("offers_meta"))}
          </p>
        </div>
        {offersError ? (
          <p className="offers-state offers-state-err">{t("offers_empty")}</p>
        ) : offers === null ? (
          <p className="offers-state">{t("offers_loading")}</p>
        ) : offers.length === 0 ? (
          <p className="offers-state">{t("offers_empty")}</p>
        ) : (
          <div className="offers-grid">
            {offers.map((o) => {
              const tripKey = o.slug ?? o.id;
              return (
                <a
                  key={o.id}
                  className="offer-card"
                  href={tripHref(lang, tripKey)}
                  onClick={navClick(() => goToTrip(tripKey))}
                >
                  {o.image && (
                    <div
                      className="offer-img"
                      style={{ backgroundImage: `url('${o.image}')` }}
                      role="img"
                      aria-label={o.name}
                    />
                  )}
                  <div className="offer-body">
                    {o.price && (
                      <div className="offer-price">{formatPrice(o.price, lang)}</div>
                    )}
                    <h3 className="offer-name">{o.name}</h3>
                    {o.description && (
                      <p className="offer-deck">
                        {truncateAtWord(o.description, 240)}
                      </p>
                    )}
                    <span className="offer-cta">
                      <span>{cText("homepage_offers_intro", "cta", t("offers_cta"))}</span> →
                    </span>
                  </div>
                </a>
              );
            })}
          </div>
        )}
      </section>

      {/* ===== Where to start ===== */}
      <section className="where-to-start" id="where-to-start">
        <div className="wts-head">
          <h2 className="wts-title">
            {cText("homepage_entry_points", "heading", `${t("wts_t1")}${t("wts_t2")}`)}
          </h2>
          <p className="wts-meta">{cText("homepage_entry_points", "meta", t("wts_meta"))}</p>
        </div>
        <div className="wts-grid">
          {(() => {
            const cards = cItems("homepage_entry_points", "cards");
            const ctas = cItems("homepage_entry_points", "ctas");
            return [1, 2, 3].map((n, i) => {
              const t1 = `wts${n}_t1` as DictKey;
              const t2 = `wts${n}_t2` as DictKey;
              const tp = `wts${n}_p` as DictKey;
              const tc = `wts${n}_cta` as DictKey;
              const card = cards[i];
              const cta = ctas[i];
              const title = card?.title?.trim() || `${t(t1)}${t(t2)}`;
              const body = card?.body?.trim() || t(tp);
              const ctaLabel = cta?.title?.trim() || t(tc);
              return (
                <a key={n} className="wts-card" href="#" onClick={stub}>
                  <div
                    className="wts-img"
                    style={{ backgroundImage: `url('${cImage("homepage_entry_points", `image_${i + 1}`, WTS_IMAGES[i]!)}')` }}
                  />
                  <div className="wts-body">
                    <div className="wts-num">0{n}</div>
                    <h3>{title}</h3>
                    <p className="wts-deck">{body}</p>
                    <span className="wts-cta">
                      <span>{ctaLabel}</span> →
                    </span>
                  </div>
                </a>
              );
            });
          })()}
        </div>
      </section>

      {/* ===== Club ===== */}
      <section className="club">
        <div className="club-inner">
          <div className="club-l">
            <h3 className="mark">
              {cText("homepage_club", "mark", `${t("club_m1")}${t("club_m2")}`)}
            </h3>
            <p className="body">
              {cText("homepage_club", "body", `${t("club_b1")}${t("club_b2")}`)}
            </p>
            <a className="cta" href="#" onClick={stub}>
              <span>{cText("homepage_club", "cta", t("club_cta"))}</span> →
            </a>
          </div>
          <div className="club-r">
            {(() => {
              const perks = cItems("homepage_club", "perks");
              return [1, 2, 3, 4].map((n) => {
                const h1 = `perk${n}_h1` as DictKey;
                const h2 = `perk${n}_h2` as DictKey;
                const p = `perk${n}_p` as DictKey;
                const perk = perks[n - 1];
                const title = perk?.title?.trim() || `${t(h1)}${t(h2)}`;
                const body = perk?.body?.trim() || t(p);
                return (
                  <div key={n} className="perk">
                    <div className="pn">0{n}</div>
                    <h5>{title}</h5>
                    <p>{body}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* ===== Testimony ===== */}
      <section className="testimony">
        <div className="t-head">
          <h2>
            {cText("homepage_testimonials", "heading", `${t("t_t1")}${t("t_t2")}`)}
          </h2>
        </div>
        <div className="tg">
          {(() => {
            const quotes = cItems("homepage_testimonials", "quotes");
            return [1, 2, 3].map((n, i) => {
              const q1 = `t${n}_q1` as DictKey;
              const q2 = `t${n}_q2` as DictKey;
              const who = `t${n}_who` as DictKey;
              const fallbackNames = ["Marta R.", "David & Sara K.", "Elena V."];
              const item = quotes[i];
              const authorName = item?.title?.trim() || fallbackNames[i]!;
              // Body convention: first line = quote (markdown bold marks the
              // "roman" emphasis), second line = who-meta. Falls back to dict.
              const rawBody = (item?.body ?? "").trim();
              // Quote text may span multiple lines; the LAST line is the
              // attribution. If there's only one line, the quote is that
              // single line and the dict attribution is used.
              const [quoteLine, whoLine] = rawBody
                ? splitLastLine(rawBody)
                : ["", ""];
              return (
                <div key={n} className="tcard">
                  <blockquote>
                    &ldquo;
                    {quoteLine ? (
                      renderQuoteWithBold(quoteLine)
                    ) : (
                      <>
                        <span>{t(q1)}</span>
                        <span className="roman">{t(q2)}</span>
                      </>
                    )}
                    &rdquo;
                  </blockquote>
                  <cite>
                    <div
                      className="av"
                      style={{ backgroundImage: `url('${cImage("homepage_testimonials", `avatar_${i + 1}`, TESTIMONY_AVATARS[i]!)}')` }}
                    />
                    <div className="who">
                      {authorName}
                      <small>{whoLine || t(who)}</small>
                    </div>
                  </cite>
                </div>
              );
            });
          })()}
        </div>
      </section>

      {/* ===== Closing ===== */}
      <section
        className="closing"
        id="get-started"
        style={{ backgroundImage: `url('${cImage("homepage_closing", "background_image", CLOSING_BG)}')` }}
      >
        <div className="closing-grain" />
        <div className="closing-inner">
          <div className="closing-kicker">
            {cText("homepage_closing", "kicker", t("closing_k"))}
          </div>
          <h2>
            {cText("homepage_closing", "headline", `${t("closing_t1")}${t("closing_t2")}`)}
          </h2>
          <p>{cText("homepage_closing", "deck", t("closing_p"))}</p>
          <div className="ctas">
            <a className="btn-primary" href="#" onClick={stub}>
              <span>{cText("homepage_closing", "cta_primary", t("closing_cta1"))}</span>
            </a>
            <a className="btn-ghost" href="#where-to-start">
              <span>{cText("homepage_closing", "cta_secondary", t("closing_cta2"))}</span>
            </a>
          </div>
        </div>
      </section>

      {/* ===== Footer ===== */}
      <footer>
        <div className="foot-grid">
          <div>
            <img
              className="foot-mark-logo"
              src={LOGO_CREAM}
              alt="The Bougie Travel Club"
            />
            <p className="foot-blurb">{cText("footer", "blurb", t("foot_blurb"))}</p>
            <div className="foot-social" aria-label="Follow Bougie">
              <a href="#" onClick={stub} aria-label="Instagram">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="5" />
                  <circle cx="12" cy="12" r="4" />
                  <circle cx="17.5" cy="6.5" r="0.6" fill="currentColor" />
                </svg>
              </a>
              <a href="#" onClick={stub} aria-label="Facebook">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 3h-2.5A3.5 3.5 0 0 0 9 6.5V9H6.5v3H9v9h3v-9h2.5l.5-3H12V6.5a.5.5 0 0 1 .5-.5H15V3z" />
                </svg>
              </a>
              <a href="#" onClick={stub} aria-label="X (Twitter)">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M17.5 3h3.2l-7 8 8.3 10h-6.5l-5.1-6.5L4.6 21H1.4l7.5-8.6L1 3h6.6l4.6 6.1L17.5 3zm-1.1 16h1.7L7.8 4.8H6L16.4 19z" />
                </svg>
              </a>
              <a href="#" onClick={stub} aria-label="TikTok">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M16.5 3a5.4 5.4 0 0 0 4.5 4v3a8.4 8.4 0 0 1-4.5-1.3v6.6a5.7 5.7 0 1 1-5.7-5.7c.3 0 .5 0 .8.1v3.2a2.5 2.5 0 1 0 1.8 2.4V3h3.1z" />
                </svg>
              </a>
              <a href="#" onClick={stub} aria-label="YouTube">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round">
                  <rect x="2" y="5" width="20" height="14" rx="3.5" />
                  <path d="M10.5 9v6l5-3z" fill="currentColor" stroke="none" />
                </svg>
              </a>
              <a href="#" onClick={stub} aria-label="LinkedIn">
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <path d="M4.5 3.5a2 2 0 1 0 0 4 2 2 0 0 0 0-4zM3 9h3v12H3V9zm5.5 0h2.9v1.6h.1c.4-.8 1.5-1.7 3.1-1.7 3.3 0 3.9 2.2 3.9 5V21h-3v-5.6c0-1.3 0-3-1.9-3s-2.2 1.5-2.2 3V21H8.5V9z" />
                </svg>
              </a>
            </div>
          </div>
          <div>
            <h5>{cText("footer", "column_travel_heading", t("foot_h1"))}</h5>
            <ul>
              {(() => {
                const links = cItems("footer", "column_travel");
                const fallback: DictKey[] = ["foot_l1", "foot_l2", "foot_l3", "foot_l4", "foot_l5"];
                return fallback.map((key, i) => (
                  <li key={i}>
                    <a href="#" onClick={stub}>{links[i]?.title?.trim() || t(key)}</a>
                  </li>
                ));
              })()}
            </ul>
          </div>
          <div>
            <h5>{cText("footer", "column_club_heading", t("foot_h2"))}</h5>
            <ul>
              {(() => {
                const links = cItems("footer", "column_club");
                const fallback: DictKey[] = ["foot_l6", "foot_l7", "foot_l8", "foot_l9"];
                return fallback.map((key, i) => (
                  <li key={i}>
                    <a href="#" onClick={stub}>{links[i]?.title?.trim() || t(key)}</a>
                  </li>
                ));
              })()}
            </ul>
          </div>
          <div className="foot-sub">
            <h5>{cText("footer", "subscribe_heading", t("foot_sub_h"))}</h5>
            <p>{cText("footer", "subscribe_deck", t("foot_sub_p"))}</p>
            <form className="foot-sub-form" onSubmit={handleSubscribe}>
              <input
                type="email"
                required
                placeholder={t("foot_sub_ph")}
                value={subEmail}
                onChange={(e) => setSubEmail(e.target.value)}
                disabled={subBusy}
              />
              <button className="foot-sub-btn" type="submit" disabled={subBusy}>
                {subBusy ? "…" : t("foot_sub_btn")}
              </button>
            </form>
            <span className="foot-sub-fine">
              {cText("footer", "subscribe_fine", t("foot_sub_fine"))}
            </span>
            {subMsg && (
              <span className={`foot-sub-msg${subMsg.kind === "err" ? " is-err" : ""}`}>
                {subMsg.text}
              </span>
            )}
          </div>
          <div>
            <h5>{cText("footer", "column_fineprint_heading", t("foot_h4"))}</h5>
            <ul>
              {(() => {
                const links = cItems("footer", "column_fineprint");
                const fallback: DictKey[] = ["foot_l14", "foot_l15", "foot_l16", "foot_l17"];
                return fallback.map((key, i) => (
                  <li key={i}>
                    <a href="#" onClick={stub}>{links[i]?.title?.trim() || t(key)}</a>
                  </li>
                ));
              })()}
            </ul>
          </div>
        </div>
        <div className="foot-bottom">
          <span>{cText("footer", "copyright", "© 2026 Bougie Travel Club S.L. · Valencia, España")}</span>
          <span>{cText("footer", "registration", t("foot_reg"))}</span>
        </div>
      </footer>
    </>
  );
}

